import type { FinancePnlSnapshot, FinancePnlWindow } from '@nadoutong/contracts';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';
import type { BrandFilter } from './revenue.ts';

const BASE = '/v1/ops/finance';

export interface FinancePnlQuery {
  readonly window: FinancePnlWindow;
  readonly brand: BrandFilter;
  readonly from?: string;
  readonly to?: string;
}

export const FINANCE_QUERY_KEYS = {
  pnl: (query: FinancePnlQuery) => [
    'finance',
    'pnl',
    query.window,
    query.brand,
    query.window === 'custom' ? query.from ?? '' : '',
    query.window === 'custom' ? query.to ?? '' : '',
  ] as const,
};

export async function getFinancePnl(
  query: FinancePnlQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<FinancePnlSnapshot> {
  const qs = new URLSearchParams({ window: query.window });
  if (query.brand.length > 0) qs.set('brand', query.brand);
  if (query.window === 'custom') {
    if (query.from !== undefined && query.from.length > 0) qs.set('from', query.from);
    if (query.to !== undefined && query.to.length > 0) qs.set('to', query.to);
  }
  return opsApiRequest<FinancePnlSnapshot>(`${BASE}/pnl?${qs.toString()}`, { signal }, fetchImpl);
}

export function useFinancePnl(
  query: FinancePnlQuery,
): UseQueryResult<FinancePnlSnapshot> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: FINANCE_QUERY_KEYS.pnl(query),
    queryFn: ({ signal }) => getFinancePnl(query, fetchImpl, signal),
  });
}
